import { NetworkStatus, useMutation, useQuery } from '@apollo/client'

import { gql } from '@/clients/utoniq-core'
import {
  AcceptTradeRequestMutation,
  AcceptTradeRequestMutationVariables,
  CancelTradeRequestMutation,
  CancelTradeRequestMutationVariables,
  CommentBoxFragmentDoc,
  CommentsConnectionFragmentDoc,
  CreateTradeCommentMutation,
  CreateTradeCommentMutationVariables,
  DeclineTradeRequestMutation,
  DeclineTradeRequestMutationVariables,
  MyTradeDetailQuery,
  MyTradeDetailQueryVariables,
  TradeBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ tradeNegotiationId: string }>()
  const { analytics } = useAnalytics()
  const { data, fetchMore, networkStatus, refetch } = useQuery<
    MyTradeDetailQuery,
    MyTradeDetailQueryVariables
  >(QUERY, {
    variables: { tradeNegotiationId: router.query.tradeNegotiationId },
    returnPartialData: true,
  })

  const [createTradeComment] = useMutation<
    CreateTradeCommentMutation,
    CreateTradeCommentMutationVariables
  >(CREATE_TRADE_COMMENT)

  const [acceptTradeRequest, { loading: acceptTradeRequesting }] = useMutation<
    AcceptTradeRequestMutation,
    AcceptTradeRequestMutationVariables
  >(ACCEPT_TRADE_REQUEST)

  const [declineTradeRequest, { loading: declineTradeRequesting }] =
    useMutation<
      DeclineTradeRequestMutation,
      DeclineTradeRequestMutationVariables
    >(DECLINE_TRADE_REQUEST)

  const [cancelTradeRequest, { loading: cancelTradeRequesting }] = useMutation<
    CancelTradeRequestMutation,
    CancelTradeRequestMutationVariables
  >(CANCEL_TRADE_REQUEST)

  return {
    state: {
      loading: networkStatus === NetworkStatus.loading,
      fetching: networkStatus === NetworkStatus.fetchMore,
      hasNextPage: !!data?.tradeNegotiation?.comments?.pageInfo?.hasNextPage,
      acceptTradeRequesting,
      declineTradeRequesting,
      cancelTradeRequesting,
      messages: data?.tradeNegotiation?.comments ?? null,
      user: data?.user ?? null,
      trade: data?.tradeNegotiation ?? null,
    },
    actions: {
      async onReply(comment) {
        await createTradeComment({
          variables: {
            createTradeCommentId: router.query.tradeNegotiationId,
            inputs: comment,
          },
        }).then(() => {
          analytics?.track('click_sendComment', {
            applicationKey: config.applicationKey,
            tradeNegotiationId: router.query.tradeNegotiationId,
            name: 'myTrade',
          })
        })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            tradeNegotiationId: router.query.tradeNegotiationId,
            pageInput: {
              cursor: data?.tradeNegotiation.comments?.pageInfo.endCursor,
              limit: 30,
            },
          },
        })
      },
      async onAcceptTradeRequest(tradeCandidateId, acceptComment) {
        const comment = acceptComment?.content ? acceptComment : null
        await acceptTradeRequest({
          variables: {
            inputs: {
              id: router.query.tradeNegotiationId,
              tradeCandidateId,
              acceptComment: comment,
            },
          },
        }).then(() => {
          analytics?.track('click_acceptTrade', {
            applicationKey: config.applicationKey,
            tradeNegotiationId: router.query.tradeNegotiationId,
            withMessage: !!comment,
          })
        })
        await refetch()
      },
      async onDeclineTradeRequest(tradeCandidateId, declineComment) {
        const comment = declineComment?.content ? declineComment : null
        await declineTradeRequest({
          variables: {
            inputs: {
              id: router.query.tradeNegotiationId,
              tradeCandidateId,
              declineComment: comment,
            },
          },
        }).then(() => {
          analytics?.track('click_declineTrade', {
            applicationKey: config.applicationKey,
            tradeNegotiationId: router.query.tradeNegotiationId,
            withMessage: !!comment,
          })
        })
        await refetch()
      },
      async onCancelTradeRequest(tradeCandidateId) {
        await cancelTradeRequest({
          variables: {
            inputs: { id: router.query.tradeNegotiationId, tradeCandidateId },
          },
        }).then(() => {
          analytics?.track('click_cancelTrade', {
            applicationKey: config.applicationKey,
            tradeNegotiationId: router.query.tradeNegotiationId,
          })
        })
      },
    },
  }
})

const QUERY = gql`
  query MyTradeDetail($tradeNegotiationId: ID!, $pageInput: PageInput) {
    user {
      id
      displayName
      avatar {
        uri
        smallUri
      }
    }
    tradeNegotiation(id: $tradeNegotiationId) {
      expiredAt
      formattedExpiredAt @client
      ...TradeBox
      comments(pageInput: $pageInput) {
        ...CommentsConnection
      }
    }
  }
  ${TradeBoxFragmentDoc}
  ${CommentsConnectionFragmentDoc}
`

const CREATE_TRADE_COMMENT = gql`
  mutation CreateTradeComment(
    $createTradeCommentId: String!
    $inputs: CommunityCommentInput!
  ) {
    createTradeComment(id: $createTradeCommentId, inputs: $inputs) {
      ...CommentBox
    }
  }
  ${CommentBoxFragmentDoc}
`

const DECLINE_TRADE_REQUEST = gql`
  mutation DeclineTradeRequest($inputs: DeclineTradeRequestInput!) {
    declineTradeRequest(inputs: $inputs) {
      id
    }
  }
`

const ACCEPT_TRADE_REQUEST = gql`
  mutation AcceptTradeRequest($inputs: AcceptTradeRequestInput!) {
    acceptTradeRequest(inputs: $inputs) {
      id
    }
  }
`

const CANCEL_TRADE_REQUEST = gql`
  mutation CancelTradeRequest($inputs: CancelTradeRequestInput!) {
    cancelTradeRequest(inputs: $inputs) {
      ...TradeBox
    }
  }
  ${TradeBoxFragmentDoc}
`
